import React, { useEffect, useMemo, useState } from 'react';

// Utils
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { useMediaQuery } from 'react-responsive';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import translationSchema from '../../../validations/products/translationSchema';
import languages from '../../../constants/languages';
import useNotification from '../../../utils/hooks/useNotification';
import productTypes from '../../../constants/productTypes';
import useForm from '../../../utils/hooks/useForm';

// Icons
import { ReactComponent as TransIcon } from '../../../assets/icons/file.svg';

// Services
import useCampService from '../../../services/campService';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';
import PageSection from '../../../components/layout/PageSection/PageSection';
import Translations from '../../../components/views/establishment/Products/Common/Translations/Translations';

function CampTranslation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  // URL query params
  const [searchParams] = useSearchParams();
  const campId = useMemo(() => searchParams.get('id'), [searchParams]);
  const langCode = useMemo(() => searchParams.get('lang'), [searchParams]);
  useEffect(() => { if (!campId || !langCode) navigate('/products/camps'); }, []);

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  const [initialValues, setInitialValues] = useState({
    title: '',
    description: '',
  });

  const [camp, setCamp] = useState(null);

  const { getCamp, putCampTranslations } = useCampService();

  const getFormattedTranslations = (translations) => {
    const translationsByCode = translations?.find((trans) => trans.language === languages[langCode]);

    return {
      title: translationsByCode?.title || '',
      description: translationsByCode?.description || '',
    };
  };

  const { isLoading: isLoadingPutTranslations, mutate: putTranslationsMutate } = useMutation(
    (translations) => putCampTranslations({ campId, langCode, translations }),
    {
      onSuccess: (res) => {
        displayNotification({
          message: t('camps.translations.form.success'),
        });

        if (res?.data?.translations) {
          setInitialValues(getFormattedTranslations(res.data.translations));
        }
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'camps.translations.form.error'),
          color: 'error',
        });
      },
    },
  );

  const { isFetching } = useQuery('getCamp', () => getCamp(campId), {
    enabled: !!campId,
    onSuccess: (res) => {
      if (res?.data?.translations) {
        setCamp(res.data);
        setInitialValues(getFormattedTranslations(res.data.translations));
      }
    },
    onError: () => {
      navigate('/products/camps');
    },
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        putTranslationsMutate({
          title: !isEmpty(values.title) ? values.title : undefined,
          description: !isEmpty(values.description) ? values.description : undefined,
        });
      }
    },
    validationSchema: translationSchema.required,
    validateOnBlur: true,
  });

  return (
    <PageWrapper
      title={camp?.name}
      breadcrumb={camp?.name ? [
        { label: t('camps.title'), href: '/products/camps' },
      ] : null}
    >
      {isFetching ? (
        <>
          <SkeletonLoader height={25} className="m-b-20" />
          <SkeletonLoader height={70} className="m-b-20" />
          <SkeletonLoader height={300} />
        </>
      ) : (
        <PageSection
          title={`${t('camps.translations.title')} - ${t(`lang.${langCode}`)}`}
          icon={<TransIcon />}
        >
          <Translations
            formik={{
              values: formik.values,
              handleChange: formik.handleChange,
              handleBlur: formik.handleBlur,
              setFieldValue: formik.setFieldValue,
              setFieldTouched: formik.setFieldTouched,
              touched: formik.touched,
              errors: formik.errors,
            }}
            initialValues={{ description: initialValues?.description }}
            productType={productTypes.camps}
          />

          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isLoadingPutTranslations}
              disabled={!formik.dirty}
            >
              {t('common.saveModifications')}
            </Button>
          </div>
        </PageSection>
      )}
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(CampTranslation),
    {
      scopes: [SCOPE.update_ownEst_camp],
    },
  ),
);
